/**
 * i18n Context for managing language state across the application
 */

import { createContext, useContext, useState, ReactNode } from 'react';
import {
  LanguageCode,
  t as translate,
  getDefaultLanguage,
  setLanguage as saveLanguage,
  getSupportedLanguages,
  applyDocumentLanguage,
} from '../i18n/i18n';

interface I18nContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  supportedLanguages: Record<LanguageCode, string>;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const lang = getDefaultLanguage();
    applyDocumentLanguage(lang);
    return lang;
  });

  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
    saveLanguage(lang);
  };

  const t = (key: string, params?: Record<string, string | number>) => {
    return translate(key, language, params);
  };

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage,
        t,
        supportedLanguages: getSupportedLanguages(),
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

